import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import { useTranslations } from '../lib/useTranslations';
import { useTheme } from '../components/shared/ThemeProvider';
import { fetchAppConfig } from '../lib/app-config';
import { AuthenticatedHomePage } from '../components/home/AuthenticatedHomePage';
import { AnonymousHomePage } from '../components/home/AnonymousHomePage';
import { ROUTES } from '../lib/routes';
import { useAuth } from '../lib/auth';
import styles from '../styles/Home.module.css';

type PageMode = 'loading' | 'anonymous' | 'authenticated';

const Home = () => {
  const router = useRouter();
  const [pageMode, setPageMode] = useState<PageMode>('loading');
  const { theme } = useTheme();
  const themeConfig = theme;
  const translations = useTranslations();
  const t = translations.home;
  const { authManager, isLoading, isInitialized, refreshUser } = useAuth();

  // Check authentication status on mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      // Wait for auth to initialize
      if (!isInitialized || isLoading) {
        return;
      }

      try {
        // Check authentication directly (will read from cache if available)
        const authenticated = await authManager.isAuthenticated();

        if (authenticated) {
          // Update context state
          await refreshUser();
          setPageMode('authenticated');
          return;
        }

        // Check if OIDC is enabled and redirect if needed
        const config = await fetchAppConfig();
        if (config.oidcEnabled) {
          // User is not authenticated and OIDC is enabled - redirect to /auth page
          router.push(ROUTES.AUTH);
          return;
        }

        // OIDC is disabled, show anonymous mode
        setPageMode('anonymous');
      } catch (error) {
        console.error('Failed to check auth status:', error);
        // On error, default to anonymous mode
        setPageMode('anonymous');
      }
    };

    checkAuthStatus();
  }, [router, authManager, isLoading, isInitialized, refreshUser]);

  // Render loading state
  if (pageMode === 'loading') {
    return (
      <>
        <Head>
          <title>{themeConfig.title}</title>
          {themeConfig && <link rel="icon" href={themeConfig.favicon} />}
        </Head>
        <main className={styles.main} data-lk-theme="default">
          <div>{t.loading}</div>
        </main>
      </>
    );
  }

  // Render authenticated home page (OIDC enabled, authenticated)
  if (pageMode === 'authenticated') {
    return (
      <AuthenticatedHomePage
        themeConfig={themeConfig}
        translations={translations}
      />
    );
  }

  // Render anonymous home page (OIDC disabled)
  return (
    <AnonymousHomePage
      themeConfig={themeConfig}
      translations={translations}
    />
  );
};

export default Home;
